import { DollarSign, PieChart } from '@tamagui/lucide-icons';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { ConfirmDelete } from '@unistocks/components/delete-button/delete-button';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { Page } from '@unistocks/components/page/page';
import { Paginator } from '@unistocks/components/paginator/paginator';
import { asyncStateTransition } from '@unistocks/components/query-state/async-state';
import { QueryState } from '@unistocks/components/query-state/query-state';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { getLocation, Table } from '@unistocks/components/table/table';
import { mapSortingToParam } from '@unistocks/components/table/utils';
import { toast } from '@unistocks/components/toast';
import { selectTransactionFilter } from '@unistocks/features/portfolios/store/portfolio.slice';
import { TransactionDetails } from '@unistocks/features/portfolios/transactions/components/transaction-details';
import { PortfolioTransactionFilter } from '@unistocks/features/portfolios/transactions/components/transaction-filter';
import { TransactionListHeader } from '@unistocks/features/portfolios/transactions/components/transaction-list-header';
import { TransactionRow } from '@unistocks/features/portfolios/transactions/components/transaction-row';
import { TransactionDto } from '@unistocks/proxy/axios-client';
import {
  transactionsGetListQueryKey,
  TransactionsGetListTransactionsQueryParameters as TransactionsGetListQueryParameters,
  useTransactionsDeleteMutationWithParameters,
  useTransactionsGetListQuery
} from '@unistocks/proxy/axios-client/TransactionsQuery';
import { useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { t } from '@unistocks/shared/localization/index.ts';
import { withFeedback } from '@unistocks/shared/utils/haptics/with-feedback';
import { HasId } from '@unistocks/types/has-id';
import { useCallback, useState } from 'react';
import { useControllableState } from 'tamagui';

export const Transactions: React.FC<HasId> = ({ id }) => {
  const queryClient = useQueryClient();
  const filter = useAppSelector(selectTransactionFilter(id));
  const { type, status, sorting, minDate, maxDate, minAmount, maxAmount, maxResultCount } = filter;

  const [selectedTransaction, setSelectedTransaction] = useState<TransactionDto | null>(null);

  const [open, onOpenChange] = useControllableState({
    defaultProp: false,
    onChange: (open: boolean) => {
      if (!open) {
        setSelectedTransaction(null);
      }
    }
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [prevFilterKey, setPrevFilterKey] = useState(() => JSON.stringify(filter));

  const filterKey = JSON.stringify(filter);
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey);
    setCurrentPage(1);
  }

  const transactionsQueryParams: TransactionsGetListQueryParameters = {
    portfolioId: id,
    type: type ?? undefined,
    status: status ?? undefined,
    minDate: minDate ?? undefined,
    maxDate: maxDate ?? undefined,
    minAmount: minAmount ?? undefined,
    maxAmount: maxAmount ?? undefined,
    sorting: mapSortingToParam(sorting),
    skipCount: (currentPage - 1) * maxResultCount,
    maxResultCount
  };

  const { data, isSuccess, isPending, isRefetching, refetch } = useTransactionsGetListQuery(
    transactionsQueryParams,
    {
      placeholderData: keepPreviousData
    }
  );

  const transactions: TransactionDto[] = data?.items ?? [];

  const { mutate: deleteTransaction, isPending: isPendingDeleteTransaction } =
    useTransactionsDeleteMutationWithParameters({
      onMutate: () => onOpenChange(false),
      onSuccess: () => {
        toast.success(t('::Transaction:Deleted'));
        queryClient.invalidateQueries();
      }
    });

  const paginate = useCallback((nextPage: number) => {
    setCurrentPage(nextPage);
  }, []);

  return (
    <Page size="medium">
      <Page.ScrollView
        refreshing={isRefetching}
        onRefresh={refetch}
        headerLogo={false}
        headerTitle={t('::Transactions')}
        headerIcon={<PortfolioTransactionFilter />}
        breadcrumbs={[
          {
            key: 'portfolio',
            href: { pathname: '/(tabs)/portfolios/[id]', params: { id } },
            name: t('::Menu:Portfolios'),
            rootPathName: 'portfolios',
            Icon: props => <PieChart {...props} />
          }
        ]}
      >
        <Table
          br={'$3'}
          transition={'medium'}
          animateOnly={['opacity', 'transform']}
          enterStyle={{ o: 0, y: -20 }}
        >
          <TransactionListHeader portfolioId={id} isLoading={isPending} />

          <QueryState
            queryKeys={[transactionsGetListQueryKey(transactionsQueryParams)]}
            loadingText={t('::Transactions:Loading')}
            isEmpty={isSuccess && !transactions.length}
            noDataIcon={DollarSign}
            noDataText={t('::Transaction:NoData')}
          >
            <Table.Body key={'table-body'} {...asyncStateTransition}>
              {transactions.map((transaction, i) => (
                <TransactionRow
                  key={transaction.id}
                  transaction={transaction}
                  rowLocation={getLocation(i, transactions)}
                  onLongPress={() => {
                    withFeedback();
                    setSelectedTransaction(transaction);
                    onOpenChange(true);
                  }}
                />
              ))}
            </Table.Body>
            <Table.Foot key={'table-foot'} {...asyncStateTransition}>
              <Table.Row>
                <Table.Cell alignCells={{ x: 'center' }}>
                  <Paginator
                    flex={1}
                    itemsPerPage={maxResultCount}
                    currentPage={currentPage}
                    totalItems={data?.totalCount ?? 0}
                    onPageChange={paginate}
                  />
                </Table.Cell>
              </Table.Row>
            </Table.Foot>
          </QueryState>
        </Table>
      </Page.ScrollView>

      <Sheet
        open={open}
        onOpenChange={onOpenChange}
        headerTitle={t('::Transaction')}
        headerIcon={<HeaderIcon Icon={DollarSign} color={'$success'} />}
        headerRight={
          <ConfirmDelete
            message={t('::Transaction:Delete:Confirm')}
            onConfirm={() =>
              selectedTransaction && deleteTransaction({ id: selectedTransaction.id })
            }
            isLoading={isPendingDeleteTransaction}
          />
        }
      >
        {selectedTransaction ? <TransactionDetails transaction={selectedTransaction} /> : null}
      </Sheet>
    </Page>
  );
};

Transactions.displayName = 'Transactions';
